import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * O que faz: Exibe imagem ampliada com animação suave de zoom ao abrir e ao fechar.
 * Onde usa: ChatWindow (mensagens de imagem no chat).
 * Conexões: Props imageUrl, isOpen, onClose, originRect, onAfterClose.
 * Tabelas: Não se aplica.
 * Data: 14/05/2026
 */
interface ImageModalProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
  originRect?: DOMRect | null;
  onAfterClose?: () => void;
}

export const ImageModal = ({ imageUrl, isOpen, onClose, originRect, onAfterClose }: ImageModalProps) => {
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  // Mantém uma duração mais perceptível para a imagem não abrir nem fechar de forma abrupta.
  const ANIMATION_DURATION_MS = 480;

  // Cria um frame inicial reduzido antes do zoom-in para o navegador conseguir interpolar a abertura.
  useEffect(() => {
    if (!isOpen) {
      setIsAnimatingOpen(false);
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setIsAnimatingOpen(true);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [isOpen]);

  // Espera a animação de saída acabar antes de desmontar para evitar corte seco no zoom-out.
  useEffect(() => {
    if (isOpen) return;
    const timer = window.setTimeout(() => {
      onAfterClose?.();
    }, ANIMATION_DURATION_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOpen, onAfterClose]);

  // Fecha com ESC e bloqueia scroll de fundo para manter foco na visualização.
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Download direto para manter o comportamento já esperado pelo usuário.
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `imagem-${Date.now()}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Se não há origem nem abertura, não renderiza nada.
  if (!isOpen && !originRect) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)',
        opacity: isAnimatingOpen ? 1 : 0,
        transition: `opacity ${ANIMATION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
      }}
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center"
        style={{
          // Faz o zoom partir e voltar para o centro da miniatura clicada.
          transformOrigin: originRect
            ? `${originRect.left + originRect.width / 2}px ${originRect.top + originRect.height / 2}px`
            : 'center center',
          transform: isAnimatingOpen ? 'scale(1)' : 'scale(0.82)',
          opacity: isAnimatingOpen ? 1 : 0,
          transition: `transform ${ANIMATION_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${ANIMATION_DURATION_MS}ms ease`
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute -top-12 right-0 z-10 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownload}
            className="border-white/30 bg-card/20 text-white hover:bg-card/30"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="border-white/30 bg-card/20 text-white hover:bg-card/30"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <img
          src={imageUrl}
          alt="Imagem ampliada"
          className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          style={{
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}
        />

        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 transform text-center text-sm text-white/70">
          Toque fora da imagem ou pressione ESC para fechar
        </div>
      </div>
    </div>
  );
};
